import React, {
  useEffect, useState, useMemo, useCallback, useRef, useContext
} from 'react';
import proptypes from 'prop-types';
import {
  params, useDataModel, extend,
  arrayOf as arrayOfType,
  string as stringType,
  number as numberType,
  bool as boolType,
  shape as shapeType
} from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';
import { MiniListsSummaryPlaceholder } from './MiniListsSummaryPlaceholder';
import { InfiniteLoader } from '../../../common/InfiniteLoader';
import { NOT_FETCHED } from '../../../AddToConstants';
import { AddToContext } from '../../../AddToContext';
import { MiniListItem } from './MiniListItem';

const {
  string, shape, number, arrayOf, objectOf, func, bool
} = proptypes;

const NUMBER_PLACEHOLDERS = 6;
const PAGE_SIZE = 12;

export const MiniListsSummary = ({
  onListClick,
  selectedListItems,
  onItemSelectAll,
  currentListId,
  isProjects
}) => {
  const { storeId } = useStore({ varnish: true });
  const [page, setPage] = useState(1);
  const [userLists, setUserLists] = useState([]);
  const [checkedLists, setCheckedLists] = useState([]);
  const [listItems, setListItems] = useState({});
  const showFirstLoading = useRef(true);
  const { setCanShowWarning } = useContext(AddToContext);

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('user-lists.ready'); }, []);

  const onFetchListComplete = useCallback((listId, fetchedList) => {
    if (fetchedList && listId) {
      const groups = fetchedList?.listProductDetails?.groups || [];
      const items = groups.flatMap((group) => (group.items || []).filter((item) => item.itemId).map((item) => {
        const base = {
          itemId: item.itemId,
          quantity: item.quantity || 1,
          groupName: group.groupName || null,
        };
        if (isProjects) {
          return { ...base, sku: NOT_FETCHED };
        }
        return { ...base, basePrice: NOT_FETCHED };
      })
      );
      setListItems((prevItems) => ({
        ...prevItems,
        [listId]: items
      }));
      // If this list is checked and we haven't selected its items yet, select them
      if (checkedLists.includes(listId) && !selectedListItems[listId]?.length && items.length > 0) {
        onItemSelectAll(listId, items);
      }
    }
  }, [checkedLists, selectedListItems, onItemSelectAll]);

  useEffect(() => {
    const selected = Object.keys(selectedListItems).filter((id) => selectedListItems[id]?.length > 0);
    setCheckedLists(selected);
  }, [selectedListItems]);

  const {
    data, error, loading
  } = useDataModel('lists', {
    variables: {
      page: page || 1,
      pageSize: PAGE_SIZE
    },
    context: { withAuth: true }
  });

  const { lists } = data || {};
  const pagination = lists?.pagination || {};
  const { totalResults } = pagination;

  const isIndeterminate = useCallback((listData) => {
    const { id, itemCount } = listData || {};
    return checkedLists.includes(id) && itemCount > 0 && (
      selectedListItems?.[id]?.length < itemCount
    );
  }, [checkedLists, selectedListItems]);

  const handleChange = useCallback((selectedListId) => {
    const selectedList = userLists.find((listData) => listData.id === selectedListId);
    if (isIndeterminate(selectedList) || !checkedLists.includes(selectedListId)) {
      setCheckedLists((prevCheckedLists) => [...prevCheckedLists, selectedListId]);
      setCanShowWarning(true);

      if (listItems?.[selectedListId]?.length) {
        onItemSelectAll(selectedListId, listItems[selectedListId]);
      }
    } else {
      setCheckedLists((prevCheckedLists) => prevCheckedLists.filter((id) => id !== selectedListId));
      onItemSelectAll(selectedListId, []);
    }
  }, [checkedLists, listItems, userLists, onItemSelectAll]);

  const handleOnSelect = useCallback((list) => {
    onListClick(list);
  }, [onListClick]);

  useEffect(() => {
    if (lists?.results) {
      setUserLists((prevItems) => [...prevItems, ...lists.results]);
    }
  }, [lists]);

  // When listItems gets populated for checked lists, trigger onItemSelectAll
  useEffect(() => {
    checkedLists.forEach((listId) => {
      if (listItems?.[listId]?.length > 0) {
        // Only call if we haven't already selected ALL items for this list
        const currentSelectedItems = selectedListItems[listId] || [];
        const listItemsCount = listItems[listId].length;
        if (currentSelectedItems.length < listItemsCount) {
          onItemSelectAll(listId, listItems[listId]);
        }
      }
    });
  }, [listItems, checkedLists, onItemSelectAll, selectedListItems]);

  const loadMoreItem = useCallback(() => {
    setPage((prevPage) => prevPage + 1);
  }, []);

  const filterdUserLists = useMemo(() => {
    const nonEmptyLists = userLists?.filter(
      (list) => list.id !== currentListId && list.itemCount > 0
    ) || [];
    if (showFirstLoading.current && (userLists?.length > 0 || error?.length > 0)) {
      showFirstLoading.current = false;
    }

    if (nonEmptyLists?.length === 0) {
      if (userLists?.length > 0 && userLists?.length < totalResults) {
        loadMoreItem();
      }
    }
    return nonEmptyLists;
  }, [userLists, currentListId, totalResults, error]);

  const isChecked = useCallback((list) => {
    const { id, itemCount } = list || {};
    return checkedLists.includes(id) || (
      selectedListItems?.[id]?.length === itemCount && itemCount > 0
    );
  }, [checkedLists, selectedListItems]);

  const hasList = useMemo(() => Boolean(filterdUserLists?.length), [filterdUserLists]);

  return (
    <>
      {
        showFirstLoading.current && (<MiniListsSummaryPlaceholder numberPlaceholders={NUMBER_PLACEHOLDERS} />)
      }
      {hasList && (
        <InfiniteLoader
          loadMoreItem={loadMoreItem}
          totalItemsCount={totalResults}
          loadedItemsCount={userLists.length}
          wrapperClassName="sui-flex sui-flex-col sui-w-full sui-gap-7"
        >
          {
            filterdUserLists?.map((list) => (
              <MiniListItem
                list={list}
                key={list.id}
                isChecked={isChecked}
                handleChange={handleChange}
                isIndeterminate={isIndeterminate}
                handleOnSelect={handleOnSelect}
                storeId={storeId}
                onFetchListComplete={onFetchListComplete}
              />
            ))
          }
        </InfiniteLoader>
      )}
      {
        (hasList && loading) && (<MiniListsSummaryPlaceholder numberPlaceholders={NUMBER_PLACEHOLDERS} />)
      }
    </>
  );
};

MiniListsSummary.propTypes = {
  selectedListItems: objectOf(arrayOf(shape({
    itemId: string,
    quantity: number
  }))),
  onListClick: func.isRequired,
  onItemSelectAll: func.isRequired,
  currentListId: string.isRequired,
  isProjects: bool.isRequired,
};

MiniListsSummary.defaultProps = {
  selectedListItems: {}
};

MiniListsSummary.displayName = 'CopyListsSummary';

MiniListsSummary.dataModel = extend({
  lists: params({ page: numberType(), pageSize: numberType() }).shape({
    pagination: shapeType({
      pageSize: numberType(),
      totalResults: numberType(),
      totalPages: numberType(),
      hasMore: boolType()
    }),
    results: arrayOfType({
      id: stringType(),
      name: stringType(),
      url: stringType(),
      itemCount: numberType(),
      listAccessType: stringType(),
      images: arrayOfType(shapeType({
        url: stringType()
      }))
    })
  })
}, MiniListItem);
